import {ReactNode, useContext} from 'react';
import {Trans} from '@ui/i18n/trans';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useAllSocialLoginsDisabled} from '@common/auth/ui/use-all-social-logins-disabled';
import {useThemeSelector} from '@ui/themes/theme-selector-context';

export enum AccountSettingsId {
  AccountDetails = 'account-details',
  SocialLogin = 'social-login',
  Password = 'password',
  TwoFactor = 'two-factor',
  LocationAndLanguage = 'location-and-language',
  Developers = 'developers',
  DeleteAccount = 'delete-account',
  Sessions = 'sessions',
}

export function AccountSettingsSidenav() {
  const p = AccountSettingsId;
  const {hasPermission} = useAuth();
  const {api} = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const allSocialsDisabled = useAllSocialLoginsDisabled();
  const {selectedTheme} = useThemeSelector();
  const isDarkMode = selectedTheme?.is_dark ?? true;

  const containerBg = isDarkMode ? '#0b1120' : '#ffffff';
  const containerBorder = isDarkMode ? '#1e293b' : '#e2e8f0';

  const scrollToPanel = (panelId: string) => {
    const el = document.getElementById(panelId);
    if (el) {
      const yOffset = -90; // Navbar offset
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
  };

  return (
    <>
      {/* Desktop Sticky Sidenav */}
      <aside
        className="hidden lg:block"
        style={{
          width: '270px',
          flexShrink: 0,
          position: 'sticky',
          top: '90px',
        }}
      >
        <div
          style={{
            backgroundColor: containerBg,
            border: `1px solid ${containerBorder}`,
            borderRadius: '20px',
            padding: '12px',
            boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <div style={{ padding: '8px 12px 12px', borderBottom: `1px solid ${containerBorder}`, marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              Navigation Menu
            </span>
          </div>

          {auth.accountSettingsPanels?.map(panel => (
            <Item
              key={panel.id}
              icon={<panel.icon viewBox="0 0 50 50" />}
              panel={panel.id as AccountSettingsId}
              onClick={() => scrollToPanel(panel.id)}
            >
              <Trans {...panel.label} />
            </Item>
          ))}

          <Item
            icon={<i className="fa-solid fa-user" style={{ width: '16px', textAlign: 'center', color: '#38bdf8' }}></i>}
            panel={p.AccountDetails}
            onClick={() => scrollToPanel(p.AccountDetails)}
          >
            <Trans message="Account details" />
          </Item>

          {!allSocialsDisabled && (
            <Item
              icon={<i className="fa-solid fa-share-nodes" style={{ width: '16px', textAlign: 'center', color: '#818cf8' }}></i>}
              panel={p.SocialLogin}
              onClick={() => scrollToPanel(p.SocialLogin)}
            >
              <Trans message="Social login" />
            </Item>
          )}

          <Item
            icon={<i className="fa-solid fa-lock" style={{ width: '16px', textAlign: 'center', color: '#f59e0b' }}></i>}
            panel={p.Password}
            onClick={() => scrollToPanel(p.Password)}
          >
            <Trans message="Password" />
          </Item>

          <Item
            icon={<i className="fa-solid fa-shield-halved" style={{ width: '16px', textAlign: 'center', color: '#10b981' }}></i>}
            panel={p.TwoFactor}
            onClick={() => scrollToPanel(p.TwoFactor)}
          >
            <Trans message="Two factor authentication" />
          </Item>

          <Item
            icon={<i className="fa-solid fa-laptop" style={{ width: '16px', textAlign: 'center', color: '#a855f7' }}></i>}
            panel={p.Sessions}
            onClick={() => scrollToPanel(p.Sessions)}
          >
            <Trans message="Active sessions" />
          </Item>

          <Item
            icon={<i className="fa-solid fa-globe" style={{ width: '16px', textAlign: 'center', color: '#06b6d4' }}></i>}
            panel={p.LocationAndLanguage}
            onClick={() => scrollToPanel(p.LocationAndLanguage)}
          >
            <Trans message="Location and language" />
          </Item>

          {api?.integrated && hasPermission('api.access') ? (
            <Item
              icon={<i className="fa-solid fa-code" style={{ width: '16px', textAlign: 'center', color: '#ec4899' }}></i>}
              panel={p.Developers}
              onClick={() => scrollToPanel(p.Developers)}
            >
              <Trans message="Developers" />
            </Item>
          ) : null}

          <div style={{ height: '1px', backgroundColor: containerBorder, margin: '6px 0' }} />

          <Item
            icon={<i className="fa-solid fa-trash-can" style={{ width: '16px', textAlign: 'center', color: '#f43f5e' }}></i>}
            panel={p.DeleteAccount}
            isDanger
            onClick={() => scrollToPanel(p.DeleteAccount)}
          >
            <Trans message="Delete account" />
          </Item>
        </div>
      </aside>

      {/* Mobile/Tablet Horizontal Scrolling Pill Bar */}
      <div
        className="block lg:hidden mb-24 w-full"
        style={{
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          paddingBottom: '8px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{ display: 'inline-flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => scrollToPanel(p.AccountDetails)}
            style={{
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '12.5px',
              fontWeight: 700,
              backgroundColor: isDarkMode ? '#131d31' : '#ffffff',
              border: `1px solid ${containerBorder}`,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa-solid fa-user" style={{ color: '#38bdf8' }}></i>
            Details
          </button>

          <button
            type="button"
            onClick={() => scrollToPanel(p.Password)}
            style={{
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '12.5px',
              fontWeight: 700,
              backgroundColor: isDarkMode ? '#131d31' : '#ffffff',
              border: `1px solid ${containerBorder}`,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa-solid fa-lock" style={{ color: '#f59e0b' }}></i>
            Password
          </button>

          <button
            type="button"
            onClick={() => scrollToPanel(p.TwoFactor)}
            style={{
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '12.5px',
              fontWeight: 700,
              backgroundColor: isDarkMode ? '#131d31' : '#ffffff',
              border: `1px solid ${containerBorder}`,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa-solid fa-shield-halved" style={{ color: '#10b981' }}></i>
            2FA
          </button>

          <button
            type="button"
            onClick={() => scrollToPanel(p.Sessions)}
            style={{
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '12.5px',
              fontWeight: 700,
              backgroundColor: isDarkMode ? '#131d31' : '#ffffff',
              border: `1px solid ${containerBorder}`,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa-solid fa-laptop" style={{ color: '#a855f7' }}></i>
            Sessions
          </button>

          <button
            type="button"
            onClick={() => scrollToPanel(p.LocationAndLanguage)}
            style={{
              padding: '8px 16px',
              borderRadius: '9999px',
              fontSize: '12.5px',
              fontWeight: 700,
              backgroundColor: isDarkMode ? '#131d31' : '#ffffff',
              border: `1px solid ${containerBorder}`,
              color: isDarkMode ? '#f8fafc' : '#0f172a',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i className="fa-solid fa-globe" style={{ color: '#06b6d4' }}></i>
            Language
          </button>
        </div>
      </div>
    </>
  );
}

interface ItemProps {
  children: ReactNode;
  icon: ReactNode;
  panel: AccountSettingsId;
  isDanger?: boolean;
  onClick: () => void;
}

function Item({children, icon, isDanger, onClick}: ItemProps) {
  const {selectedTheme} = useThemeSelector();
  const isDarkMode = selectedTheme?.is_dark ?? true;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        borderRadius: '12px',
        border: 'none',
        backgroundColor: 'transparent',
        color: isDanger ? '#f43f5e' : (isDarkMode ? '#cbd5e1' : '#334155'),
        fontSize: '13.5px',
        fontWeight: 600,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
      className={
        isDanger
          ? 'hover:bg-rose-500/10 hover:text-rose-400'
          : isDarkMode
            ? 'hover:bg-slate-800/80 hover:text-amber-400'
            : 'hover:bg-slate-100 hover:text-amber-600'
      }
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </span>
      <span>{children}</span>
    </button>
  );
}
